// src/pages/onboarding/analysis.tsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useOnboarding } from "@/contexts/onboarding-context";
import { useOnboardingData } from "@/contexts/onboarding-data-context";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Bug, RotateCcw } from "lucide-react";

const DEBUG = process.env.NODE_ENV === "development";

const analysisMessages = [
  "Scanning your campaigns for hidden gems...",
  "Uncovering Performance Max channel mysteries...",
  "Reviewing your search terms for comedy gold...",
  "Checking if your keywords are actually working...",
  "Warming up the AI models...",
  "Looking for budget leaks (spoiler: we'll probably find some)",
  "Calculating how much money you're leaving on the table...",
  "Hunting down underperforming placements...",
  "Measuring your profit curves with scientific precision...",
  "Discovering which campaigns deserve more love...",
  "Finding the keywords that are secretly superstars...",
  "Evaluating your hourly patterns for optimization ideas...",
  "Identifying campaigns that need a budget intervention...",
  "Preparing your personalized profit recommendations...",
  "Analyzing Performance Max like a detective...",
];

const OnboardingAnalysisPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setStage } = useOnboarding();
  const {
    fetchOnboardingData,
    loadingStatus,
    error,
    data,
    clearOnboardingData,
  } = useOnboardingData();

  const [progress, setProgress] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [animationClass, setAnimationClass] = useState(
    "opacity-100 transform scale-100",
  );
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const analysisStarted = useRef(false);

  // Get debug mode from previous page or localStorage
  useEffect(() => {
    const debugMode =
      location.state?.debugMode ||
      localStorage.getItem("onboarding-debug-mode") === "true";
    setIsDebugMode(debugMode);
    if (debugMode) {
      localStorage.setItem("onboarding-debug-mode", "true");
    }
  }, [location.state]);

  useEffect(() => {
    const webAppUrl = location.state?.webAppUrl;
    // Ensure analysis only runs once on mount with a valid URL
    if (webAppUrl && !analysisStarted.current) {
      analysisStarted.current = true;
      console.log(
        `[OnboardingAnalysis] Starting fresh analysis for ${webAppUrl}`,
      );
      clearOnboardingData();
      fetchOnboardingData(webAppUrl);
    }
  }, [location.state, clearOnboardingData, fetchOnboardingData]);

  // Handle message rotation with smooth transitions
  useEffect(() => {
    if (loadingStatus === "loading") {
      const messageInterval = setInterval(() => {
        setAnimationClass("opacity-0 transform scale-95");

        setTimeout(() => {
          setCurrentMessageIndex((prev) =>
            prev >= analysisMessages.length - 1 ? 0 : prev + 1,
          );
          setAnimationClass("opacity-100 transform scale-100");
        }, 150);
      }, 2000);

      return () => clearInterval(messageInterval);
    }
  }, [loadingStatus]);

  // Handle progress bar animation
  useEffect(() => {
    if (loadingStatus === "loading") {
      const timer = setInterval(() => {
        setProgress((prev) => (prev >= 90 ? 90 : prev + 5));
      }, 800);
      return () => clearInterval(timer);
    }

    if (loadingStatus === "success" && data) {
      const ESSENTIAL_TABS = [
        "account",
        "keyword80",
        "search80",
        "quality",
        "on_performance",
        "on_settings",
        "on_disapproved",
        "on_empty_ad_groups",
      ];

      const allEssentialDataFetched = ESSENTIAL_TABS.every(
        (tab) => data[tab] !== undefined,
      );

      if (allEssentialDataFetched) {
        const hasCoreData =
          data.account &&
          data.account.length > 0 &&
          data.keyword80 &&
          data.keyword80.length > 0;
        if (hasCoreData) {
          setProgress(100);
          // Only auto-redirect if debug mode is off
          if (!isDebugMode) {
            setTimeout(() => {
              setStage("CHOOSE");
              navigate("/onboarding/results");
            }, 500);
          }
        } else {
          setAnalysisError(
            "Analysis complete, but your account seems to be missing essential keyword or campaign data. Please check your Google Ads account or try again.",
          );
          setProgress(100); // Show completion, but with an error.
        }
      } else {
        const missingTabs = ESSENTIAL_TABS.filter(
          (tab) => data[tab] === undefined,
        );
        setAnalysisError(
          `Failed to fetch required data for: ${missingTabs.join(", ")}. Please try again.`,
        );
        setProgress(100);
      }
    }
  }, [loadingStatus, data, setStage, navigate, isDebugMode]);

  const resetAnalysis = () => {
    analysisStarted.current = false;
    setAnalysisError(null);
    setCurrentMessageIndex(0);
    setProgress(0);
    setAnimationClass("opacity-100 transform scale-100");
    // Re-fetch data to restart the analysis
    const webAppUrl = location.state?.webAppUrl;
    fetchOnboardingData(webAppUrl);
  };

  const goToStart = () => {
    localStorage.setItem("onboarding-debug-mode", isDebugMode.toString());
    navigate("/onboarding/start", { state: { debugMode: isDebugMode } });
  };

  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-6">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Analyzing Your
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                {" "}
                Google Ads Account
              </span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Our AI agent is crunching the numbers and looking for quick wins
            </p>
          </div>

          {/* Main Analysis Card */}
          <Card
            className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large animate-scale-in text-center"
            style={{ animationDelay: "200ms" }}
          >
            <CardHeader>
              <CardTitle className="text-2xl mb-4">
                Analysis in Progress
              </CardTitle>

              {/* Animated message container with fixed height */}
              <div className="h-16 flex items-center justify-center mb-6">
                <CardDescription
                  className={`text-lg transition-all duration-300 ease-in-out ${animationClass}`}
                >
                  {analysisError ||
                    (error
                      ? `An error occurred: ${error}`
                      : analysisMessages[currentMessageIndex])}
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Progress bar */}
              <div className="space-y-2">
                <Progress value={progress} className="w-full h-4" />
                <p className="text-sm text-muted-foreground">
                  {progress}% complete
                </p>
              </div>

              <p className="text-sm text-muted-foreground">
                {analysisError
                  ? "Please try again or check your data source."
                  : "This usually takes under a minute. Please don't close this page."}
              </p>

              {loadingStatus === "success" &&
                !isDebugMode &&
                !analysisError && (
                  <div className="animate-pulse">
                    <p className="text-success font-medium">
                      Analysis complete! Redirecting to results...
                    </p>
                  </div>
                )}

              {loadingStatus === "success" && isDebugMode && !analysisError && (
                <div className="space-y-4">
                  <p className="text-success font-medium">
                    Analysis complete! (Auto-redirect disabled in debug mode)
                  </p>
                  <Button
                    onClick={() => {
                      setStage("CHOOSE");
                      navigate("/onboarding/results");
                    }}
                    className="w-full sm:w-auto"
                  >
                    Continue to Results
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Debug Panel - Only visible in development */}
      {DEBUG && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Bug className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Debug Panel</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isDebugMode}
                    onCheckedChange={(checked) => {
                      setIsDebugMode(checked);
                      localStorage.setItem(
                        "onboarding-debug-mode",
                        checked.toString(),
                      );
                    }}
                    id="debug-mode"
                  />
                  <label htmlFor="debug-mode" className="text-sm">
                    Debug Mode
                  </label>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={resetAnalysis}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Reset Analysis
                  </Button>
                  <Button
                    onClick={goToStart}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    Go to Start
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sign-in Footer */}
      <div
        className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 animate-fade-in"
        style={{ animationDelay: "800ms" }}
      >
        <p className="text-sm text-muted-foreground bg-background/80 backdrop-blur-sm rounded-lg px-4 py-2 border border-border/50">
          Already have an account?{" "}
          <Link
            to="/sign-in"
            className="text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
          >
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default OnboardingAnalysisPage;
